import { useEffect, useState } from 'react';
import { CalendarClock } from 'lucide-react';

import {
  trialCountdownLabel,
  trialOffers,
  trialTrayState,
  type CheckoutFrom,
  type EngagementView,
} from '../shared/engagement';
import type { CapturePointV6LicenseView } from '../shared/desktop-api';
import { reportEvent } from './telemetry';
import { TooltipButton } from './TooltipButton';
import './engagement.css';

/**
 * The trial countdown and the engagement state the License screen reads
 * (ADR-0080; owner decisions 5 and 7, 2026-09-25).
 *
 * Nothing here interrupts: the countdown is a status-bar tray item that exists
 * only near the end of the trial. The "Your first week" checklist and its email
 * line in the Scan jobs window were removed (owner, 2026-10-03: "it shouldn't
 * even be there, none of it"); nudges for people who skip the demo are the
 * demo's own work.
 */

/** What the License screen should open on, when something sent the person there. */
let pendingLicenseIntent: 'buy' | 'extend' | 'more-time' | null = null;
export const takeLicenseIntent = () => {
  const intent = pendingLicenseIntent;
  pendingLicenseIntent = null;
  return intent;
};
/**
 * Where the person came to the License screen from, for telemetry (`checkout.start`'s `from`): set by what sent
 * them there, taken by the License screen. Nothing set: they opened it themselves.
 */
let pendingLicenseFrom: CheckoutFrom | null = null;
export const noteLicenseFrom = (from: CheckoutFrom) => {
  pendingLicenseFrom = from;
};
export const takeLicenseFrom = () => {
  const from = pendingLicenseFrom;
  pendingLicenseFrom = null;
  return from;
};
export const openLicenseWith = (intent: 'buy' | 'extend' | 'more-time', from?: CheckoutFrom) => {
  pendingLicenseIntent = intent;
  if (from) pendingLicenseFrom = from;
  window.dispatchEvent(new Event('capturepoint-open-license'));
  window.dispatchEvent(new CustomEvent('capturepoint-license-intent', { detail: intent }));
};

export function useEngagement(): EngagementView | null {
  const [view, setView] = useState<EngagementView | null>(null);
  useEffect(() => {
    const api = window.capturePointDesktop;
    if (!api?.getEngagement) return;
    let live = true;
    void api
      .getEngagement()
      .then((next) => {
        if (live) setView(next);
      })
      .catch(() => undefined);
    const stop = api.onEngagementChanged?.((next) => {
      if (live) setView(next);
    });
    return () => {
      live = false;
      stop?.();
    };
  }, []);
  return view;
}

export function useLicenseView(): CapturePointV6LicenseView | null {
  const [state, setState] = useState<CapturePointV6LicenseView | null>(null);
  useEffect(() => {
    const api = window.capturePointDesktop;
    if (!api?.getV6LicenseState) return;
    let live = true;
    void api
      .getV6LicenseState()
      .then((next) => {
        if (live) setState(next);
      })
      .catch(() => undefined);
    const stop = api.onV6LicenseStateChanged((next) => {
      if (live) setState(next);
    });
    // The countdown moves on its own between license answers.
    const tick = window.setInterval(
      () =>
        setState((current) =>
          current && current.expiresUtc
            ? {
                ...current,
                secondsRemaining: Math.max(
                  0,
                  Math.ceil((Date.parse(current.expiresUtc) - Date.now()) / 1_000),
                ),
              }
            : current,
        ),
      60_000,
    );
    return () => {
      live = false;
      stop();
      window.clearInterval(tick);
    };
  }, []);
  return state;
}

/**
 * The trial countdown, as status (interface principles: tray items exist only
 * while something needs the operator): near the end of the trial and after it,
 * a small item beside This computer that opens License on Buy now.
 */
export function TrialTray() {
  const license = useLicenseView();
  const shown =
    license &&
    (license.runtimeState === 'trial-active' || license.runtimeState === 'trial-expired') &&
    trialOffers(license).nearEnd;
  const seconds =
    !license || license.runtimeState === 'trial-expired' ? 0 : (license.secondsRemaining ?? 0);
  const tray = shown ? trialTrayState(seconds) : null;
  const trayState = tray?.state ?? null;
  const hoursLeft = tray?.hoursLeft ?? null;
  // Did they see the countdown: once a session for each of its states (main keeps it to once).
  useEffect(() => {
    if (trayState && hoursLeft)
      reportEvent(`trial.tray-shown:${trayState}`, { attrs: { hours_left: hoursLeft } });
  }, [trayState, hoursLeft]);
  if (!shown) return null;
  const label =
    seconds <= 0 ? 'Trial ended' : trialCountdownLabel(seconds).replace(' in your trial', '');
  return (
    <TooltipButton
      type="button"
      data-telemetry="trial-tray"
      className={`trial-tray${seconds <= 0 ? ' ended' : ''}`}
      aria-label={`${label}. Open License to buy`}
      tooltip="Buy now"
      onMouseDown={(event) => event.preventDefault()}
      onClick={() => openLicenseWith('buy', 'tray')}
    >
      <CalendarClock size={16} aria-hidden="true" />
      <span>{label}</span>
    </TooltipButton>
  );
}

/** Says the trial (or the license) has ended: the error a refused start shows on the workspace. */
const trialWall = /trial.*has ended|license has expired/i;
/** Main's refusal because the trial or license ended (the product's own sentence), whichever action met it. */
export const isTrialWall = (message: string): boolean => trialWall.test(message);

/**
 * View license options, beside a workspace error that says the trial or license ended. Hitting the trial wall is
 * recorded once a session (`trial.expired-gate`); a buy that follows started from it (`from` expired-gate).
 */
export function TrialWallButton({ message }: { message: string }) {
  const wall = trialWall.test(message);
  const trialEnded = wall && /trial.*has ended/i.test(message);
  useEffect(() => {
    if (trialEnded) reportEvent('trial.expired-gate');
  }, [trialEnded]);
  if (!wall) return null;
  return (
    <button
      type="button"
      data-telemetry="view-license-options"
      onClick={() => {
        noteLicenseFrom('expired-gate');
        window.dispatchEvent(new Event('capturepoint-open-license'));
      }}
    >
      View license options
    </button>
  );
}
